import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { API_BASE } from '../../lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, Printer, CheckCircle, User, Brain, ClipboardList, MapPin } from 'lucide-react';

const DIMENSION_COLORS = {
  conscientiousness: '#0071e3', openness: '#34C759', stability: '#FF9500',
  agreeableness: '#AF52DE', extraversion: '#FF2D55',
};

const CounsellorReportView = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recommendation, setRecommendation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/counsellor/report/${reportId}`)
      .then(res => {
        setReport(res.data);
        if (res.data.recommendation) setRecommendation(res.data.recommendation);
      })
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [reportId]);

  const handleSubmit = async () => {
    if (!recommendation.trim()) { toast.error('Please enter your recommendation'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/counsellor/recommendation/${reportId}`, { recommendation });
      toast.success('Recommendation submitted! Document sent to apprentice.');
      setReport(prev => ({ ...prev, status: 'reviewed', recommendation }));
    } catch (err) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-12 flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0071e3]" /></div>;
  if (!report) return <div className="p-12 text-center text-[#86868b]">Report not found</div>;

  const rd = report.report_data || {};
  const resume = rd.resume || {};
  const p = resume.personal || {};
  const e = resume.education || {};
  const w = resume.work || {};
  const sk = resume.skills || {};
  const personality = rd.personality || {};
  const pType = personality.type || {};
  const pScores = personality.scores || {};
  const aptitude = rd.aptitude_scores || {};
  const dimOrder = ['conscientiousness', 'openness', 'stability', 'agreeableness', 'extraversion'];

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      {/* Header */}
      <div className="bg-white border-b border-[#d2d2d7] px-6 py-4 flex items-center justify-between print:hidden">
        <button onClick={() => navigate('/counsellor/dashboard')} className="flex items-center gap-2 text-sm text-[#86868b] hover:text-[#1d1d1f]">
          <ArrowLeft className="w-4 h-4" /> Back to Inbox
        </button>
        <div className="flex gap-3">
          <Button onClick={() => window.print()} variant="outline" className="rounded-full border-[#d2d2d7]">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 sm:p-8">
        {/* ===== DOCUMENT ===== */}
        <div className="bg-white rounded-2xl border border-[#d2d2d7] print:rounded-none print:border-0 overflow-hidden" style={{ fontFamily: 'Arial, Helvetica, sans-serif' }}>

          {/* PAGE 1: CV */}
          <div className="p-8 sm:p-10 print:p-8">
            <div className="text-center mb-6 pb-4 border-b border-gray-200">
              <p className="text-xs text-[#86868b] uppercase tracking-wider mb-1">ApprenticeshipMatch.ie</p>
              <h1 className="text-xl font-bold text-black">Candidate Assessment Report</h1>
              <p className="text-xs text-[#86868b] mt-1">Ref: {rd.candidate_ref}</p>
            </div>

            {/* CV Section */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-black uppercase tracking-wide">{p.firstName} {p.lastName}</h2>
              <p className="text-xs text-gray-600">
                {[p.county, p.phone, p.email, p.dob ? `DOB: ${p.dob}` : null].filter(Boolean).join('  |  ')}
              </p>
            </div>

            {/* Personal Statement */}
            {(() => {
              const val = resume.values || {};
              const handWritten = val.personalStatement?.trim();
              const autoStmt = (val.environment || val.goals?.length > 0) ? [
                val.environment ? `I am seeking an apprenticeship in a ${val.environment.toLowerCase()} environment.` : '',
                val.goals?.length > 0 ? `My goals include ${val.goals.map(g => g.toLowerCase()).join(', ')}.` : '',
                val.topValues?.length > 0 ? `I value ${val.topValues.map(v => v.toLowerCase()).join(', ')} in my work.` : '',
                'I am highly motivated, reliable and committed to completing a structured apprenticeship programme that combines hands-on experience with formal qualification.',
              ].filter(Boolean).join(' ') : null;
              const stmt = handWritten || autoStmt;
              return stmt ? (
                <DocSection title="Personal Statement">
                  <p className="text-xs text-gray-700 leading-relaxed">{stmt}</p>
                </DocSection>
              ) : null;
            })()}

            {e.school && (
              <DocSection title="Education">
                <p className="text-xs font-semibold text-black">{e.school}</p>
                {e.level && <p className="text-xs text-gray-700">{e.level}</p>}
                {(e.lcResults || []).length > 0 && (
                  <ul className="ml-4 mt-1 space-y-0.5">
                    {e.lcResults.map(r => <li key={r.subject} className="text-xs text-gray-700 list-disc">{r.subject}{r.grade ? ` — ${r.grade}` : ''}</li>)}
                  </ul>
                )}
              </DocSection>
            )}

            {w.hasWork === 'Yes' && (
              <DocSection title="Work Experience">
                {[0,1,2,3].map(i => {
                  const role = w[`role${i}`];
                  if (!role?.title) return null;
                  return (
                    <div key={i} className={i > 0 ? 'mt-3' : ''}>
                      <p className="text-xs font-semibold text-black">{role.employer}</p>
                      <p className="text-xs text-gray-700 italic">{role.title}{role.duration ? ` (${role.duration})` : ''}</p>
                      {(role.tasks || []).length > 0 && (
                        <ul className="ml-4 mt-0.5 space-y-0.5">
                          {role.tasks.map(t => <li key={t} className="text-xs text-gray-700 list-disc">{t}</li>)}
                        </ul>
                      )}
                    </div>
                  );
                })}
                {w.hasVolunteer === 'Yes' && w.volunteerDesc && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-black">Volunteering</p>
                    <p className="text-xs text-gray-700">{w.volunteerDesc}</p>
                  </div>
                )}
              </DocSection>
            )}

            {(() => {
              const allSkills = [...(sk.digital||[]), ...(sk.practical||[]), ...(sk.soft||[])];
              if (p.licence) allSkills.push(`${p.licence} Driving Licence`);
              if (p.transport === 'Yes') allSkills.push('Own transport available');
              return allSkills.length > 0 ? (
                <DocSection title="Skills & Certifications">
                  <ul className="ml-4 space-y-0.5">
                    {allSkills.map((s,i) => <li key={i} className="text-xs text-gray-700 list-disc">{s}</li>)}
                  </ul>
                </DocSection>
              ) : null;
            })()}

            {(() => {
              const int = resume.interests || {};
              const ach = resume.achievements || {};
              const hasContent = (int.list||[]).length > 0 || (ach.achievements||[]).length > 0;
              return hasContent ? (
                <DocSection title="Interests & Activities">
                  {(int.list||[]).length > 0 && (
                    <ul className="ml-4 space-y-0.5">
                      {int.list.map(item => (
                        <li key={item} className="text-xs text-gray-700 list-disc">
                          <span className="font-semibold text-black">{item}</span>{int.level ? ` — ${int.level} level` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                  {(ach.achievements||[]).length > 0 && (
                    <ul className="mt-1 ml-4 space-y-0.5">
                      {ach.achievements.map(a => <li key={a} className="text-xs text-gray-700 list-disc">{a}</li>)}
                    </ul>
                  )}
                  {(ach.leadership||[]).length > 0 && (
                    <ul className="mt-1 ml-4 space-y-0.5">
                      {ach.leadership.map(l => <li key={l} className="text-xs text-gray-700 list-disc"><span className="font-semibold text-black">Leadership:</span> {l}</li>)}
                    </ul>
                  )}
                </DocSection>
              ) : null;
            })()}

            <DocSection title="References">
              <p className="text-xs text-gray-700">Available on request.</p>
            </DocSection>
          </div>

          {/* PAGE BREAK for print */}
          <div className="border-t-2 border-dashed border-gray-200 print:break-before-page" />

          {/* PAGE 2: Personality + Aptitude */}
          <div className="p-8 sm:p-10 print:p-8">
            <div className="text-center mb-6 pb-4 border-b border-gray-200 print:block hidden">
              <p className="text-xs text-[#86868b]">ApprenticeshipMatch.ie &middot; Ref: {rd.candidate_ref}</p>
            </div>

            {/* Personality Profile */}
            <DocSection title="Section A — Personality Profile" icon={Brain}>
              {pType.type_name && (
                <div className="mb-3">
                  <p className="text-xs text-[#86868b] uppercase tracking-wider">Type</p>
                  <h3 className="text-base font-bold text-black">{pType.type_name}</h3>
                  <p className="text-xs text-[#86868b]">{pType.keywords}</p>
                </div>
              )}
              {pType.description && <p className="text-xs text-gray-700 leading-relaxed mb-4">{pType.description}</p>}

              <div className="space-y-2">
                {dimOrder.map(dim => {
                  const data = pScores[dim];
                  if (!data) return null;
                  const score = data.score || 5;
                  const color = DIMENSION_COLORS[dim] || '#0071e3';
                  return (
                    <div key={dim} className="flex items-center gap-3">
                      <span className="text-xs text-gray-700 w-40 flex-shrink-0">{data.label}</span>
                      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${score * 10}%`, backgroundColor: color }} />
                      </div>
                      <span className="text-xs font-bold text-gray-700 w-12 text-right">{score} / 10</span>
                    </div>
                  );
                })}
              </div>
            </DocSection>

            {/* Aptitude Scores */}
            <DocSection title="Section B — Aptitude Test Results" icon={ClipboardList}>
              <div className="space-y-3">
                {Object.entries(aptitude).map(([tid, data]) => (
                  <div key={tid} className="flex items-center gap-3">
                    <span className="text-xs text-gray-700 w-40 flex-shrink-0">{data.title}</span>
                    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-[#0071e3]" style={{ width: `${data.score}%` }} />
                    </div>
                    <span className="text-xs font-bold text-gray-700 w-10 text-right">{data.score}%</span>
                    {data.band && <span className="text-xs font-semibold w-24 text-right" style={{ color: data.band === 'Exceptional' ? '#34C759' : data.band === 'Strong' ? '#0071e3' : '#FF9500' }}>{data.band}</span>}
                  </div>
                ))}
              </div>
            </DocSection>

            {/* Sectors & Preferences */}
            {(rd.sectors_of_interest?.length > 0 || rd.location_preferences?.county) && (
              <DocSection title="Section C — Candidate Preferences" icon={MapPin}>
                {rd.sectors_of_interest?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs text-[#86868b]">Sectors of Interest</p>
                    <p className="text-xs text-black font-medium">{rd.sectors_of_interest.join(', ')}</p>
                  </div>
                )}
                {rd.location_preferences?.county && (
                  <div>
                    <p className="text-xs text-[#86868b]">Preferred Location</p>
                    <p className="text-xs text-black font-medium">{rd.location_preferences.county}</p>
                  </div>
                )}
              </DocSection>
            )}

            {/* Overall Recommendation */}
            <DocSection title="Section D — Overall Recommendation">
              {report.status === 'reviewed' ? (
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{report.recommendation}</p>
                </div>
              ) : (
                <div className="print:hidden">
                  <p className="text-sm text-[#86868b] mb-3">Enter your professional assessment and recommendation for this candidate:</p>
                  <textarea
                    value={recommendation}
                    onChange={e => setRecommendation(e.target.value)}
                    rows={6}
                    className="w-full p-4 border border-[#d2d2d7] rounded-xl text-sm resize-none focus:outline-none focus:border-[#0071e3]"
                    placeholder="Based on the candidate's profile, aptitude scores, and our career guidance session, I recommend..."
                    data-testid="recommendation-textarea"
                  />
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || !recommendation.trim()}
                    className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8"
                    data-testid="submit-recommendation-btn"
                  >
                    {submitting ? 'Submitting...' : 'Submit Final Recommendation'}
                  </Button>
                </div>
              )}
            </DocSection>
          </div>
        </div>
      </div>
    </div>
  );
};

const DocSection = ({ title, children, icon: Icon }) => (
  <div style={{ marginTop: '36px' }}>
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#86868b] print:hidden" />}
      <h3 className="text-xs font-bold text-black uppercase tracking-wider" style={{ letterSpacing: '0.8px' }}>{title}</h3>
    </div>
    <div className="border-t border-gray-300 pt-3">{children}</div>
  </div>
);

export default CounsellorReportView;
